/**
 * テーマ（03 5.1。値は画面イメージ 04 の「デザインの決めごと」）。
 * 色・文字・角の丸み・影・ボタン・入力欄は旧PCRM（frontend/src/index.css のライトテーマ）に合わせる。
 * breakpoints は xs:0・sm:600・md:769・lg:1280・xl:1920。モバイル＝down('md')（768px 以下）。
 */
import { alpha, createTheme } from '@mui/material/styles';
import type { Shadows } from '@mui/material/styles';
import { createElement } from 'react';

/** 旧PCRM の影（--shadow-sm〜--shadow-xl）。MUI の elevation 1〜24 に段階で割り当てる */
const LEGACY_SHADOWS = {
  sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  base: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
  md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
  lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
  xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
} as const;

function shadowOf(elevation: number): string {
  if (elevation === 0) return 'none';
  if (elevation === 1) return LEGACY_SHADOWS.sm;
  if (elevation <= 3) return LEGACY_SHADOWS.base;
  if (elevation <= 7) return LEGACY_SHADOWS.md;
  if (elevation <= 15) return LEGACY_SHADOWS.lg;
  return LEGACY_SHADOWS.xl;
}

export const LEGACY_COLORS = {
  border: '#E2E8F0',
  border_strong: '#CBD5E1',
  bg_tertiary: '#F1F5F9',
  bg_hover: '#E2E8F0',
  text_tertiary: '#64748B',
  primary_glow: 'rgba(194, 65, 12, 0.15)',
  primary_selected: 'rgba(194, 65, 12, 0.08)',
} as const;

export const LEGACY_CARD_SHADOW = LEGACY_SHADOWS.xl;

/**
 * 277：旧の画面タイトル（.content-title）の書体。Windows では和文がメイリオ／游ゴシック系で出る（新の Noto Sans JP と違う）
 */
export const LEGACY_TITLE_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif";
/** 277：旧のページ送り（Pagination.css の 1・2・3、「1-20件を表示」、「1 / 9」）の書体 */
export const LEGACY_UI_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif";

export const APP_THEME = createTheme({
  palette: {
    primary: { main: '#EA580C', dark: '#C2410C', light: '#FFEDD5', contrastText: '#FFFFFF' },
    secondary: { main: '#64748B' },
    error: { main: '#DC2626' },
    warning: { main: '#CA8A04' },
    success: { main: '#16A34A' },
    info: { main: '#2563EB' },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text: { primary: '#1E293B', secondary: '#475569' },
    divider: LEGACY_COLORS.border,
  },
  typography: {
    fontFamily: '"Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    fontSize: 14,
    h5: { fontSize: 24, fontWeight: 500 },
    // 277：旧の本文は line-height 1.6（index.css の body）。16px→25.6px、14px→22.4px
    body1: { lineHeight: 1.6 },
    body2: { lineHeight: 1.6 },
    subtitle1: { fontSize: 16, fontWeight: 500 },
    caption: { fontSize: 12 },
  },
  breakpoints: { values: { xs: 0, sm: 600, md: 769, lg: 1280, xl: 1920 } },
  shape: { borderRadius: 6 },
  shadows: Array.from({ length: 25 }, (_, elevation) => shadowOf(elevation)) as Shadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // 277：画面タイトル（h1）は旧の書体（-apple-system 系）。Typography の書体より強くするため h1.MuiTypography-root も挙げる
        'h1, h1.MuiTypography-root': { fontFamily: LEGACY_TITLE_FONT_FAMILY },
        // 277：素の <button>（❮・▲・▼・× など）はブラウザ既定の Arial になるので、旧と同じく本文の書体を継ぐ
        'button:not(.MuiButtonBase-root)': { fontFamily: 'inherit' },
        // 277：チェックボックスの四角（旧 .checkbox-item：18px・薄い灰の枠・角 4px・オン＝オレンジに白の ✓）
        '.pcrm-cb': {
          display: 'block', width: 18, height: 18, boxSizing: 'border-box', borderRadius: 4, border: '1px solid #CBD5E1',
          backgroundColor: '#FFFFFF', color: '#FFFFFF', fontSize: 12, fontWeight: 700, lineHeight: '16px', textAlign: 'center',
          transition: 'all 0.15s ease',
        },
        '.pcrm-cb--on': { borderColor: '#EA580C', backgroundColor: '#EA580C', '&::after': { content: '"✓"' } },
        '.pcrm-cb--mixed': { borderColor: '#EA580C', backgroundColor: '#EA580C', '&::after': { content: '"−"' } },
        // 277：一覧の表の選択欄（旧 .table-checkbox：16px・1.5px #e2e2e2 の枠・角 3px・オン＝黒に白のチェック）
        '.pcrm-cb-table': { display: 'block', position: 'relative', width: 16, height: 16, boxSizing: 'border-box', borderRadius: 3, border: '1.5px solid #E2E2E2', backgroundColor: '#FFFFFF' },
        '.pcrm-cb-table--on, .pcrm-cb-table--mixed': { borderColor: '#000000', backgroundColor: '#000000' },
        '.pcrm-cb-table--on::after': {
          content: '""', position: 'absolute', left: 4, top: 1, width: 4, height: 8,
          border: 'solid #FFFFFF', borderWidth: '0 2px 2px 0', transform: 'rotate(45deg)',
        },
        '.pcrm-cb-table--mixed::after': { content: '""', position: 'absolute', left: 3, top: 6, width: 7, height: 0, borderTop: '2px solid #FFFFFF' },
      },
    },
    MuiCheckbox: {
      // 277：MUI 既定（24px の濃い灰の線のアイコン）ではなく旧の四角
      defaultProps: {
        icon: createElement('span', { className: 'pcrm-cb', 'aria-hidden': true }),
        checkedIcon: createElement('span', { className: 'pcrm-cb pcrm-cb--on', 'aria-hidden': true }),
        indeterminateIcon: createElement('span', { className: 'pcrm-cb pcrm-cb--mixed', 'aria-hidden': true }),
      },
      styleOverrides: { root: { padding: 6 } },
    },
    MuiRadio: {
      // 277：旧は素の radio（黒系の輪・オンはオレンジ＝accent-color）。MUI 既定の 20px・#475569 から寄せる
      styleOverrides: { root: { padding: 6, color: '#545454', '& .MuiSvgIcon-root': { fontSize: 18 } } },
    },
    MuiInputBase: {
      styleOverrides: {
        // 277：旧の入力欄は line-height normal（新は 23px／18.7px）。高さは MUI の height で決まるので見た目の高さは変わらない
        input: { 'input&': { lineHeight: 'normal' } },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 64,
          [theme.breakpoints.down('md')]: { minHeight: 56 },
        }),
      },
    },
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small', fullWidth: true } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          backgroundColor: theme.palette.background.default,
          transition: 'box-shadow 0.2s, background-color 0.2s',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: LEGACY_COLORS.border },
          '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: LEGACY_COLORS.border_strong,
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 0 0 3px ${LEGACY_COLORS.primary_glow}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main, borderWidth: 1 },
        }),
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // ボタンの色はメインカラー（旧PCRM に合わせる）。削除（color="error"）だけは赤のまま、"inherit"・"warning" などもオレンジにする
        root: ({ ownerState, theme }) => {
          const base = {
            minHeight: 32,
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 500,
            textTransform: 'none',
          } as const;
          if (!ownerState.color || ownerState.color === 'primary' || ownerState.color === 'error') {
            return base;
          }
          const primary = theme.palette.primary;
          if (ownerState.variant === 'contained') {
            return {
              ...base,
              backgroundColor: primary.main,
              color: primary.contrastText,
              '&:hover': { backgroundColor: primary.dark },
            };
          }
          if (ownerState.variant === 'outlined') {
            return {
              ...base,
              color: primary.main,
              borderColor: alpha(primary.main, 0.5),
              '&:hover': { borderColor: primary.main, backgroundColor: alpha(primary.main, 0.04) },
            };
          }
          return { ...base, color: primary.main, '&:hover': { backgroundColor: alpha(primary.main, 0.04) } };
        },
        sizeSmall: { minHeight: 28, fontSize: 12 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        // 色を指定したアイコンボタンもメインカラーにする。削除（error）と指定なし（灰色）はそのまま
        root: ({ ownerState, theme }) =>
          ownerState.color && !['default', 'inherit', 'primary', 'error'].includes(ownerState.color)
            ? {
                color: theme.palette.primary.main,
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
              }
            : {},
      },
    },
    MuiLink: {
      styleOverrides: {
        // 表の中のリンク（顧客名・企業名など）は黒い文字。マウスを乗せたときだけ下線を出す
        root: ({ theme }) => ({
          '.MuiDataGrid-cell &, .MuiTableCell-root &': {
            color: theme.palette.text.primary,
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' },
          },
        }),
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 1 },
      styleOverrides: {
        rounded: { borderRadius: 8 },
        // 旧の .card は影ではなく 1px の線で囲む
        elevation1: { boxShadow: 'none', border: `1px solid ${LEGACY_COLORS.border}` },
      },
    },
    MuiChip: { defaultProps: { size: 'small' } },
  },
});

/** PC のサイドメニューの幅（旧PCRM の .sidebar の既定 180px に合わせる：262 r1） */
export const DRAWER_WIDTH = 180;
export const MOBILE_DRAWER_WIDTH = 280;
